/**
 * DialogChangeSecCode Component
 *
 * This component renders a dialog for changing the user's security code.
 * It includes form validation, API integration, and error handling.
 */

import { AlertState } from '@context/app-context';
import { yupResolver } from '@hookform/resolvers/yup';
import { Check, Circle, Close, SyncLockOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { SHA512 } from 'crypto-js';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { useEffect, useState } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useVerifyCredsMutation } from '../../api/users-management/user-mangement.api.rtk';
import { getColor } from '../../assets/theme';
import useAuth from '../../hooks/useAuth';
import useDisclosure from '../../hooks/useDisclosure';
import useToast from '../../hooks/useToast';
import useUser from '../../hooks/useUser';
import { handleControllerPaste, handleKeyDown } from '../../utils/keypress';
import { dialogChangeSecCodeSchema } from '../../utils/schema';
import DialogWrapper from './dialog-wrapper';

/**
 * Props for the DialogChangeSecCode component
 */
interface DialogChangeSecCodeProps {
  open: boolean;
  onClose: () => void;
  propToast?: (data: AlertState) => Promise<void>;
}

/**
 * Form values interface for the security code change form
 */
interface FormValues {
  currentSecurityCode: string;
  newSecurityCode: string;
  confirmSecurityCode: string;
}

/**
 * Interface for alert types used in the component
 */
interface AlertTypes {
  title: string;
  desc: string;
  color: string;
}

/**
 * DialogChangeSecCode component
 *
 * @param {DialogChangeSecCodeProps} props - The component props
 * @returns {JSX.Element} The rendered component
 */
const DialogChangeSecCode = ({
  open,
  onClose,
  propToast,
}: DialogChangeSecCodeProps) => {
  const { white, primary, danger, success, textSecondary } = getColor();

  const [currentSecCode, setCurrentSecCode] = useState('');
  const [alertDetails, setAlertDetails] = useState<AlertTypes>({
    title: '',
    desc: '',
    color: '',
  });
  const [_failedAttempts, setFailedAttempts] = useState(0);
  const maxAttempts = 3;

  const userData = useUser();

  const toast = !propToast ? useToast().toast : propToast;

  const { changePasswordV2, sendEmail } = useAuth(toast);

  const [verifyCreds, { isLoading, isError, reset: resetVerify }] =
    useVerifyCredsMutation();

  const [isLoadingUI, setIsLoadingUI] = useState(false);

  const startLoading = () => setIsLoadingUI(true);
  const stopLoading = () => setIsLoadingUI(false);
  const [isTempCode, setIsTempCode] = useState(false);

  const requestTemporaryCode = async () => {
    startLoading();
    await sendEmail('security_code').finally(stopLoading);
    setIsTempCode(true);
    reset();
  };

  // Reset form and close alert when dialog is closed
  useEffect(() => {
    if (!open) {
      clearErrors();
      reset();
      resetVerify();
      closeAlert();
    }
  }, [open]);

  const {
    isOpen: showAlert,
    onOpen: openAlert,
    onClose: closeAlert,
  } = useDisclosure();

  const loadBtn = useDisclosure();

  // Initialize form with react-hook-form
  const methods = useForm<FormValues>({
    defaultValues: {
      currentSecurityCode: '',
      newSecurityCode: '',
      confirmSecurityCode: '',
    },
    resolver: yupResolver(dialogChangeSecCodeSchema),
    mode: 'onChange',
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    clearErrors,
    reset,
    watch,
    setError,
    setValue,
  } = methods;

  /**
   * Handle form submission
   * @param {FormValues} data - The form data
   */
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      loadBtn.onOpen();

      const res: any = await changePasswordV2({
        route: 'security_code',
        data: {
          securityCode: data.currentSecurityCode,
          newSecurityCode: data.newSecurityCode,
          verifySecurityCode: data.confirmSecurityCode,
        },
      });

      if (typeof res == 'string' || res?.errMsg || res?.status > 299) {
        setAlertDetails({
          title: 'Error',
          desc: res?.data?.error_message || res?.errMsg || res,
          color: danger,
        });
        loadBtn.onClose();
        return openAlert();
      }

      setAlertDetails({
        title: 'Success',
        desc: 'Security Code updated successfully',
        color: success,
      });
      reset();
      openAlert();
      loadBtn.onClose();
    } catch (error) {
      loadBtn.onClose();
    }
  };

  // Update currentSecCode when the current security code input changes
  useEffect(() => {
    let debounce: any;

    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(async () => {
      setCurrentSecCode(watch('currentSecurityCode'));
    }, 1000);
  }, [watch('currentSecurityCode')]);

  // Verify current security code
  useEffect(() => {
    if (isTempCode) return;
    const debounce = setTimeout(async () => {
      if (currentSecCode?.length > 0) {
        try {
          const res: any = await verifyCreds({
            userID: userData?.userID,
            securityCode: currentSecCode,
            signature: SHA512(
              `${userData?.userID}${currentSecCode}`,
            ).toString(),
          });

          if (res?.error) {
            setFailedAttempts((prev) => {
              const attempts = prev + 1;
              if (attempts >= maxAttempts) {
                toast({
                  message: 'Max attempts verifying security code.',
                  severity: 'warning',
                });
                setFailedAttempts(0);
                onClose();
              }
              setError('currentSecurityCode', {
                type: 'manual',
                message: 'Wrong Security Code',
              });
              return attempts;
            });
          } else {
            clearErrors('currentSecurityCode');
            setFailedAttempts(0);
          }
        } catch (error) {
          console.error('Verification failed:', error);
        }
      }
    }, 1000);

    return () => clearTimeout(debounce);
  }, [currentSecCode, isTempCode]);

  const isEightDigits = (code: string) => /^\d{8}$/.test(code);
  const isAllSameDigits = (code: string) => /^(\d)\1{7}$/.test(code);
  const hasThreeConsecutive = (code: string) => {
    for (let i = 0; i < code.length - 2; i++) {
      const a = Number(code[i]);
      const b = Number(code[i + 1]);
      const c = Number(code[i + 2]);
      const isIncreasing = b === a + 1 && c === b + 1;
      const isDecreasing = b === a - 1 && c === b - 1;
      if (isIncreasing || isDecreasing) return true;
    }
    return false;
  };
  const hasThreeSameConsecutive = (code: string) => /(.)\1\1/.test(code);

  const securityCodeRules = [
    {
      label: 'Exactly 8 digits',
      check: (value: string) => isEightDigits(value),
    },
    {
      label: 'No three identical digits in a row',
      check: (value: string) => !hasThreeSameConsecutive(value),
    },
    {
      label: 'No sequences of three increasing or decreasing digits',
      check: (value: string) => !hasThreeConsecutive(value),
    },
    {
      label: 'All digits must not be identical',
      check: (value: string) => !isAllSameDigits(value),
    },
  ];

  return (
    <>
      <DialogWrapper
        open={open}
        onClose={() => {
          onClose();
          setIsTempCode(false);
        }}
      >
        <Box>
          {showAlert && (
            <Fade in={true} timeout={{ enter: 500, exit: 300 }}>
              <Alert
                variant='filled'
                sx={{
                  bgcolor: `${alertDetails.color}!important`,
                  width: { xs: '100%', sm: '528px' },
                  mb: '12px',
                  color: '#FFF',
                }}
                action={
                  <IconButton
                    color='inherit'
                    size='small'
                    onClick={closeAlert}
                    disableRipple
                    sx={{
                      bgcolor: 'transparent',
                      border: 0,
                      ':hover': {
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    <Close fontSize='inherit' />
                  </IconButton>
                }
              >
                <AlertTitle>{alertDetails.title}</AlertTitle>
                <div
                  dangerouslySetInnerHTML={{ __html: alertDetails.desc || '' }}
                />
              </Alert>
            </Fade>
          )}
          <Stack
            sx={{
              bgcolor: white,
              borderRadius: '4px',
              py: 3,
              px: 4,
              justifyContent: 'flex-start',
            }}
          >
            <Box>
              <SyncLockOutlined
                sx={{
                  width: '59px',
                  height: '59px',
                  bgcolor: '#F7F7FB',
                  p: '12px',
                  borderRadius: '100%',
                  color: primary,
                }}
              />
            </Box>
            <DialogTitle sx={{ p: '8px 0', fontWeight: 600 }}>
              Change Security Code
            </DialogTitle>

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent
                  sx={{ p: '16px 0!important', maxWidth: '880px' }}
                >
                  <Stack direction={{ xs: 'column', lg: 'row' }} gap={3}>
                    <Box sx={{ width: { xs: '100%', lg: '270px' } }}>
                      <Typography sx={{ color: textSecondary }}>
                        Security Code must follow:
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {securityCodeRules.map((rule, index) => {
                          const value = watch('newSecurityCode') || '';
                          const isValid = rule.check(value);
                          const showError =
                            errors?.newSecurityCode && value.length > 0;

                          const getIcon = () => {
                            if (isValid && value.length > 0) {
                              return (
                                <Check
                                  sx={{ color: success, width: 18, height: 18 }}
                                />
                              );
                            } else if (showError) {
                              return (
                                <Close
                                  sx={{ color: danger, width: 16, height: 16 }}
                                />
                              );
                            } else {
                              return (
                                <Circle
                                  sx={{ color: primary, width: 16, height: 16 }}
                                />
                              );
                            }
                          };

                          return (
                            <ListItem key={index} sx={{ gap: 1, p: 0 }}>
                              <ListItemIcon sx={{ minWidth: 'auto' }}>
                                {getIcon()}
                              </ListItemIcon>
                              <ListItemText
                                primary={rule.label}
                                sx={{
                                  color:
                                    showError && !isValid
                                      ? danger
                                      : textSecondary,
                                  textDecoration:
                                    isValid && value.length > 0
                                      ? 'line-through'
                                      : 'none',
                                }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>

                    <Box sx={{ width: { xs: '100%', lg: '480px' } }}>
                      {[
                        'currentSecurityCode',
                        'newSecurityCode',
                        'confirmSecurityCode',
                      ].map((item: string, i: number) => (
                        <Box key={i}>
                          <Controller
                            name={
                              item as
                                | 'currentSecurityCode'
                                | 'newSecurityCode'
                                | 'confirmSecurityCode'
                            }
                            control={control}
                            render={({ field }) => {
                              return (
                                <>
                                  <Typography
                                    variant='subtitle1'
                                    gutterBottom
                                    sx={{ textTransform: 'capitalize' }}
                                  >
                                    {`Enter ${
                                      item === 'confirmSecurityCode'
                                        ? 'Confirmed Security Code'
                                        : item
                                            ?.replace(
                                              /([a-z])([A-Z])/g,
                                              '$1 $2',
                                            )
                                            .replace(/^./, (c) =>
                                              c.toUpperCase(),
                                            )
                                    }`}

                                    {item == 'currentSecurityCode' &&
                                      ' / Temporary Code'}
                                  </Typography>
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      mb: errors[
                                        item as
                                          | 'currentSecurityCode'
                                          | 'newSecurityCode'
                                          | 'confirmSecurityCode'
                                      ]
                                        ? '8px'
                                        : '16px',
                                    }}
                                  >
                                    {isLoading &&
                                      item == 'currentSecurityCode' && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: 0,
                                            bottom: 0,
                                            right: 0,
                                            left: 0,
                                            zIndex: 2,
                                            opacity: 0.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '4px',
                                          }}
                                        >
                                          <CircularProgress size={20} />
                                        </Box>
                                      )}
                                    <MuiOtpInput
                                      {...field}
                                      onKeyDown={handleKeyDown}
                                      onPaste={(e) =>
                                        handleControllerPaste(e, setValue, item)
                                      }
                                      length={8}
                                      display='flex'
                                      flexWrap='wrap'
                                      alignItems='center'
                                      justifyContent='center'
                                      gap={1}
                                      TextFieldsProps={{
                                        size: 'small',
                                        type: 'password',
                                        required: true,
                                        sx: {
                                          pb:
                                            item == 'currentSecurityCode'
                                              ? 1
                                              : 0,
                                          maxWidth: '48px',
                                          '& .MuiInputBase-input': {
                                            height: '24px',
                                            width: { xs: '24px', md: '48px' },
                                            textTransform: 'capitalize',
                                            border: `2px solid ${primary}`,
                                            borderRadius: '4px',
                                          },
                                        },
                                        placeholder: '#',
                                        autoComplete: 'off',
                                      }}
                                      validateChar={(character, _index) =>
                                        !isNaN(Number(character))
                                      }
                                    />
                                    {item == 'currentSecurityCode' && (
                                      <Button
                                        sx={{
                                          fontSize: '12px',
                                          p: 0,
                                          ':hover': { bgcolor: 'transparent' },
                                        }}
                                        onClick={async () =>
                                          await requestTemporaryCode()
                                        }
                                      >
                                        Request Temporary Code
                                      </Button>
                                    )}
                                  </Box>
                                </>
                              );
                            }}
                          />
                          {errors[
                            item as
                              | 'currentSecurityCode'
                              | 'newSecurityCode'
                              | 'confirmSecurityCode'
                          ] && (
                            <Typography
                              sx={{
                                fontFamily: 'Poppins',
                                fontSize: 12,
                                marginBottom: 1,
                                marginLeft: 1,
                                fontWeight: 400,
                                lineHeight: '19.92px',
                                letterSpacing: '0.4px',
                                color: danger,
                              }}
                            >
                              {
                                errors[
                                  item as
                                    | 'currentSecurityCode'
                                    | 'newSecurityCode'
                                    | 'confirmSecurityCode'
                                ]?.message
                              }
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 0 }}>
                  <Stack direction='row' gap='8px'>
                    <Button variant='outlined' onClick={onClose}>
                      Cancel
                    </Button>
                    <LoadingButton
                      variant='contained'
                      type='submit'
                      disabled={isError}
                      loading={loadBtn.isOpen}
                    >
                      Update
                    </LoadingButton>
                  </Stack>
                </DialogActions>
              </form>
            </FormProvider>
          </Stack>
        </Box>
      </DialogWrapper>
      <Dialog open={isLoadingUI}>
        <Stack
          sx={{ py: 2, px: 3, justifyContent: 'center', alignItems: 'center' }}
        >
          <CircularProgress />
        </Stack>
      </Dialog>
    </>
  );
};

export default DialogChangeSecCode;
